import posthog from 'posthog-js'

declare global {
  interface Window {
    posthog: typeof posthog | null;
  }
}

const sensitivePathPrefixes = [
  '/admin',
  '/password',
  '/members/statements',
  '/statements',
]
const hasSensitivePathPrefix = () => sensitivePathPrefixes.some(prefix => window.location.pathname.startsWith(prefix))

const handlePathChange = () => {
  if (!window.posthog) return

  let started = true // Assume that it's running until proven otherwise
  try {
  // Wrap in a try/catch block to be robust to vendor-related bugs.
    started = window.posthog.sessionRecordingStarted()
  } catch (error) {
    console.error('Error checking PostHog status:', error)
  }

  if (started && hasSensitivePathPrefix()) {
    window.posthog.stopSessionRecording()
  } else if (!started && !hasSensitivePathPrefix) {
    window.posthog.startSessionRecording()
  }
}

export function initPostHog () {
  const projectApiKey = document.head.querySelector<HTMLMetaElement>('meta[name="posthog:project-api-key"]')?.content

  if (!projectApiKey) {
    return
  }

  // TODO: Instead of assigning the PostHog instance to `window.posthog`, we should instead export the instance
  // from this file and import it where needed.

  window.posthog = posthog.init(
    projectApiKey,
    {
      api_host: 'https://ph.thatch.ai',
      ui_host: 'https://app.posthog.com',
      rageclick: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*', // Must match all elements to use maskTextFn
        maskTextFn: (text, element) => {
          const isSensitiveClass = typeof element?.className === 'string' ? element.className.includes('sensitive') : false

          // Search for substrings and mask the whole thing. This is likely to
          // have some false positives, but errs on the side of caution.
          const REGEXES = [
            /\(\d{3}\) \d{3}-\d{4}/g, // Phone number: (123) 456-7890
            /[^@\s]+@[^@\s]+\.[^@\s]+/g, // Simplified email: email@example.com
            /[0-9A-Za-z!]{10}-[0-9A-Za-z]{4}/g, // Suggested password
            /\b\d{1,2}[-/]\d{1,2}[-/](?:\d{4}|\d{2})\b/g, // Dates/DOB: matches 01/01/1970, 01-01-1970, 1/1/1970, 1-1-1970, 1/1/70 or 1-1-70
            /\d{9}/g, // SSNs, account numbers, no hyphens: 123456789
            /\d{3}-\d{2}-\d{4}/g, // SSN, with hyphens: 123-45-6789
          ]

          return isSensitiveClass || REGEXES.some(regex => regex.test(text)) ? '*'.repeat(text.length) : text
        },
      },
      // Init with session recording disabled if the path is sensitive
      disable_session_recording: hasSensitivePathPrefix(),
    }
  )

  // Attach to both full page load and turbo:load events. See:
  // - https://developer.mozilla.org/en-US/docs/Web/API/Document/DOMContentLoaded_event
  // - https://turbo.hotwired.dev/reference/events#turbo%3Aload
  document.addEventListener('DOMContentLoaded', handlePathChange)
  document.addEventListener('turbo:load', handlePathChange)
}
